import PropTypes from 'prop-types';

import GoogleMapReact from 'google-map-react';
import NumberFormat from 'react-number-format';
import Slider from 'react-slick';
import { MdLocationOn } from 'react-icons/md';
import _ from 'lodash';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { googleApiKey } from '../../../constants';

import HotelRate from '../HotelRate';
import currencySymbol from '../../../helpers/currencySymbol';
import Marker from '../../DisplayMap/Marker';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import HotelDetailsContent from './HotelDetailsContent';
import sliderSettings from './sliderSettings';
import ModalSpinnerSaveTrip from '../../ModalSpinner/ModalSpinnerSaveTrip';

const HotelDetails = ({ hotelDetails }) => {
  const { isHotelRateSearch } = useSelector((store) => store.hotel);

  if (!hotelDetails || !hotelDetails.hotel) {
    return <div className="container text-center mx-auto mt-4">Hotel details not found!</div>;
  }

  const hasProducts =
    hotelDetails &&
    hotelDetails.hotel &&
    hotelDetails.hotel.products &&
    hotelDetails.hotel.products.length > 0;

  const selectedProduct = hotelDetails && hotelDetails.selectedProduct;

  const { hotel } = hotelDetails;
  const { images, geoname, address, city, zip, stateCode, products, name, lat, lon } = hotel;

  const Arrow = (props) => {
    // oxlint-disable-next-line react/prop-types
    const { className, onClick, direction } = props;

    // Set margin style dynamically based on the direction
    const marginStyle = direction === 'next' ? { marginRight: '30px' } : { marginLeft: '30px' };

    return (
      // oxlint-disable-next-line jsx-a11y/control-has-associated-label
      <div
        className={className}
        style={{
          ...marginStyle,
          zIndex: '1',
          position: 'absolute',
        }}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            onClick(event);
          }
        }}
      />
    );
  };

  const RateInfoDisplay = () => {
    if (isHotelRateSearch) {
      if (hasProducts) {
        return (
          <div className="box-border p-5 m-5 mt-0 bg-white">
            <div className="text-center font-serif italic text-base">
              <div className="text-gray-500 flex items-center justify-center mt-0">
                <span>from</span>
                <span className="px-1 inline-block text-black not-italic text-2xl">
                  <NumberFormat
                    value={
                      selectedProduct &&
                      selectedProduct.roomPrice &&
                      selectedProduct.roomPrice.averageNightlyRate
                    }
                    displayType="text"
                    thousandSeparator
                    prefix={
                      selectedProduct &&
                      selectedProduct.roomPrice &&
                      currencySymbol(selectedProduct.roomPrice.currencyCode)
                    }
                    fixedDecimalScale
                    decimalScale={2}
                  />
                </span>
                <span>per night</span>
              </div>
              <div className="-mt-1">Includes all taxes & fees</div>
            </div>
          </div>
        );
      }
      return (
        <div className="box-border p-5 m-5 bg-red-500">
          <div className="text-center font-serif italic text-base">
            No rates available for your chosen dates!
          </div>
        </div>
      );
    }

    if (!hasProducts) {
      return (
        <div className="box-border p-5 m-5 bg-white">
          <div className="text-center font-serif italic text-base">
            Change dates in search box to see rates.
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className="w-full relative">
        {images && images.length > 0 && (
          <div className="w-full h-28 relative">
            <Slider
              infinite={sliderSettings && sliderSettings.infinite}
              slidesToShow={sliderSettings && sliderSettings.slidesToShow}
              slidesToScroll={sliderSettings && sliderSettings.slidesToScroll}
              dots={sliderSettings && sliderSettings.dots}
              autoplay={sliderSettings && sliderSettings.autoplay}
              autoplaySpeed={sliderSettings && sliderSettings.autoplaySpeed}
              nextArrow={<Arrow direction="next" />}
              prevArrow={<Arrow direction="prev" />}
              customPaging={sliderSettings && sliderSettings.customPaging}
              dotsClass={sliderSettings && sliderSettings.dotsClass}
              className="w-[100%] h-full"
            >
              {images.slice(0, Math.min(images.length, 6)).map((src, index) => (
                <div key={src.url} className="w-full h-full flex justify-center items-center">
                  <img
                    src={src.url}
                    alt={`Slide ${index + 1}`}
                    className="w-full h-28 object-cover"
                  />
                </div>
              ))}
            </Slider>
          </div>
        )}

        <div className="max-w-screen-xl mx-auto mb-5 grid grid-cols-1 md:grid-cols-5 gap-1">
          <div className="w-auto bg-white-200 order-2 md:order-1 md:col-span-3 p-1">
            <div className="w-full flex flex-col flex-wrap p-6 md:p-8 space-y-4">
              {geoname && (
                <div className="flex flex-row items-center gap-1">
                  <MdLocationOn size="20" fill="#e66756" className="inline-block -mt-1 -ml-1" />
                  <span>
                    <span className="capitalize">{(geoname.city || '').toLowerCase()}</span>,{' '}
                    {geoname && geoname.stateOrCountry}
                  </span>
                </div>
              )}
              <div className="text-4xl">{name}</div>
              <hr className="border-gray-400 my-4 w-5/6" />
              {hotelDetails.hotel.starRating > 0 && (
                <span>{`${hotelDetails.hotel.starRating}-star hotel`}</span>
              )}

              <HotelDetailsContent hotelDetails={hotelDetails} />

              <div className="box-border pt-4 pb-0">
                <h3 className="font-normal text-xl">Location</h3>
                <p>{address}</p>
                <p className="capitalize">
                  {(city || '').toLowerCase()}, {zip}
                </p>
                <p>
                  {stateCode}, {geoname && geoname.countryName}
                </p>
              </div>

              {_.isNumber(lat) && _.isNumber(lon) && lat !== 0 && lon !== 0 ? (
                <div className="h-[50vh] w-90 mt-5 outline outline-[1px] outline-[rgba(0,0,0,0.25)]">
                  <div style={{ height: '50vh', width: '100%' }}>
                    <GoogleMapReact
                      yesIWantToUseGoogleMapApiInternals
                      bootstrapURLKeys={{ key: googleApiKey }}
                      center={{
                        lat,
                        lng: lon,
                      }}
                      defaultZoom={15}
                    >
                      <Marker
                        selected
                        key={hotelDetails.hotelKey}
                        lat={lat}
                        lng={lon}
                        markerKey={hotelDetails.hotelKey}
                      />
                    </GoogleMapReact>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>

          <div
            className={clsx(
              'relative box-border z-10 mb-5 w-auto order-1 md:order-2 md:col-span-2 p-1',
              { 'md:-mt-64': images && images.length > 0 },
            )}
          >
            <div className="bg-white bg-opacity-75 md:w-104 shadow-md box-border py-2">
              <div className="find-rate">
                <div className="flex items-center justify-center flex-col p-4">
                  <h1 className="text-xl font-bold">{name}</h1>
                </div>
                <RateInfoDisplay />
              </div>

              {hasProducts &&
                products &&
                products.map((product) => (
                  <HotelRate
                    key={product?.code}
                    product={product}
                    hotelDetails={hotelDetails}
                    tripId={hotelDetails.hotelKey}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
      <ModalSpinnerSaveTrip tripId={hotelDetails.hotelKey} />
    </>
  );
};

HotelDetails.propTypes = {
  hotelDetails: PropTypes.shape(),
};

HotelDetails.defaultProps = {
  hotelDetails: undefined,
};
export default HotelDetails;
